import {
  CoreProvider,
  I18nConfig,
  LoadingScreen,
  MuiLocaleProvider,
  queryConfig,
} from '@nohara-dxdev/react-core';
import { EntityProvider, OidcConfig, OidcProvider, ScopeProvider } from '@nohara-dxdev/react-oidc';
import { ThemeProvider } from '@nohara-dxdev/react-theme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { MainErrorFallback } from '@/components/errors/main';
import { env } from '@/config/env';
import enLocale from '@/locales/en.json';
import jaLocale from '@/locales/ja.json';

interface AppProviderProps {
  children: React.ReactNode;
}

export const AppProvider = ({ children }: AppProviderProps) => {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: queryConfig,
      }),
  );

  const [oidcConfig] = React.useState<OidcConfig>(() => ({
    apiEndpoint: env.API_ENDPOINT,
    oauthEndpoint: env.OAUTH_DOMAIN,
    userPoolId: env.USER_POOL_ID,
    userPoolWebClientId: env.USER_POOL_WEB_CLIENT_ID,
    oidcCallbackPath: env.OIDC_CALLBACK_PATH,
    signOutPath: env.SIGN_OUT_PATH,
    permissionDefinitions: {},
  }));

  const i18nConfig = React.useMemo(
    (): I18nConfig => ({
      resources: {
        en: {
          translation: enLocale,
        },
        ja: {
          translation: jaLocale,
        },
      },
      defaultNS: 'translation',
      fallbackNS: 'translation',
      fallbackLng: 'en',
    }),
    [],
  );

  return (
    <React.Suspense fallback={<LoadingScreen />}>
      <CoreProvider i18nConfig={i18nConfig}>
        <ErrorBoundary FallbackComponent={MainErrorFallback}>
          <QueryClientProvider client={queryClient}>
            {import.meta.env.DEV && <ReactQueryDevtools />}
            <ThemeProvider>
              <MuiLocaleProvider>
                <OidcProvider config={oidcConfig}>
                  <EntityProvider>
                    <ScopeProvider>{children}</ScopeProvider>
                  </EntityProvider>
                </OidcProvider>
              </MuiLocaleProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </CoreProvider>
    </React.Suspense>
  );
};
